import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Activity, ChevronDown, ChevronRight } from 'lucide-react';
import { activityApi } from '../services/api';
import { useDateTime } from '../contexts/DateTimeContext';
import { useTranslation } from 'react-i18next';

// ─── helpers ─────────────────────────────────────────────────────────────────

function timeAgo(isoStr, t) {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('notifications.just_now');
  if (mins < 60) return t('notifications.minutes_ago', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('notifications.hours_ago', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('notifications.days_ago', { count: days });
  return null; // caller will use formatDate fallback
}

function groupByDate(logs, formatDate, t) {
  const groups = {};
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  for (const log of logs) {
    const d = new Date(log.created_at).toDateString();
    let label;
    if (d === today) label = t('common.today');
    else if (d === yesterday) label = t('common.yesterday');
    else label = formatDate(log.created_at);
    if (!groups[label]) groups[label] = [];
    groups[label].push(log);
  }
  return Object.entries(groups);
}

const ACTION_COLORS = {
  created:        'bg-emerald-500',
  updated:        'bg-blue-500',
  deleted:        'bg-red-500',
  completed:      'bg-amber-500',
  status_changed: 'bg-purple-500',
  assigned:       'bg-cyan-500',
  comment_deleted:'bg-red-500',
};

// Actions with a translated "<action> task" / "<action> project" phrase
// (keys ui_views.activity.task_<action> and ui_views.activity.project_<action>).
const KNOWN_ACTIONS = ['created', 'updated', 'deleted', 'completed', 'status_changed', 'assigned', 'comment_deleted'];

// Translation keys of the changed field names.
const FIELD_LABELS = {
  title:          'ui_views.activity.field_title',
  priority:       'tasks.priority',
  due_date:       'tasks.due_date',
  project_id:     'tasks.project',
  section_id:     'tasks.section',
  scheduled_start:'ui_views.activity.field_scheduled_start',
  scheduled_end:  'ui_views.activity.field_scheduled_end',
  description:    'common.description',
  status:         'tasks.status',
  assigned_to:    'ui_views.activity.field_assignees',
  comment:        'ui_views.activity.field_comment',
  name:           'common.name',
  color:          'common.color',
};

function formatValue(field, val, formatDateTime) {
  if (val === null || val === undefined) return '-';
  if (Array.isArray(val)) return val.join(', ') || '-';
  if (field === 'due_date' || field === 'scheduled_start' || field === 'scheduled_end') {
    try { return formatDateTime(val); } catch { return String(val); }
  }
  if (field === 'color') {
    return (
      <span className="flex items-center gap-1">
        <span className="w-3 h-3 rounded-full inline-block" style={{ background: val }} />
        {val}
      </span>
    );
  }
  return String(val);
}

// ─── Initials avatar ─────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-amber-500', 'bg-blue-500', 'bg-emerald-500', 'bg-purple-500',
  'bg-rose-500', 'bg-cyan-500', 'bg-orange-500', 'bg-indigo-500',
];

function initials(name) {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (const c of name) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ─── Single log entry ─────────────────────────────────────────────────────────

function LogEntry({ log }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { formatTime, formatDate, formatDateTime } = useDateTime();
  const hasChanges = log.changes && Object.keys(log.changes).length > 0;
  const dot = ACTION_COLORS[log.action] || 'bg-gray-500';
  const entity = log.entity_type === 'project' ? 'project' : 'task';
  const actionText = KNOWN_ACTIONS.includes(log.action)
    ? t(`ui_views.activity.${entity}_${log.action}`)
    : `${log.action} ${t(`ui_views.activity.entity_${entity}`)}`;

  return (
    <div className="flex gap-3">
      {/* Avatar */}
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-foreground shrink-0 mt-0.5 ${avatarColor(log.user_name)}`}>
        {initials(log.user_name)}
      </div>

      <div className="flex-1 min-w-0">
        {/* Main line */}
        <p className="text-sm text-foreground leading-snug">
          <span className="font-medium text-foreground">{log.user_name}</span>
          {' '}
          <span className="text-muted-foreground">{actionText}</span>
          {log.entity_title && (
            <span className="font-medium text-foreground"> "{log.entity_title}"</span>
          )}
        </p>

        {/* Timestamp + expand toggle */}
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
          <span className="text-xs text-muted-foreground">{timeAgo(log.created_at, t) ?? formatDate(log.created_at)} · {formatTime(log.created_at)}</span>
          {hasChanges && (
            <button
              onClick={() => setExpanded(v => !v)}
              className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground/80 transition-colors"
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {t('projects.activity_details')}
            </button>
          )}
        </div>

        {/* Changes diff */}
        {expanded && hasChanges && (
          <div className="mt-2 space-y-1 rounded-lg border border-border bg-accent/60 p-2">
            {Object.entries(log.changes).map(([field, change]) => (
              <div key={field} className="text-xs">
                <span className="text-muted-foreground font-medium">{FIELD_LABELS[field] ? t(FIELD_LABELS[field]) : field}:</span>
                {' '}
                <span className="text-red-400 line-through">{formatValue(field, change.from, formatDateTime)}</span>
                {' → '}
                <span className="text-emerald-400">{formatValue(field, change.to, formatDateTime)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

export default function ActivityLogModal({ project, onClose }) {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const { formatDate } = useDateTime();

  const fetchPage = useCallback(async (reset = false) => {
    if (!project) return;
    setLoading(true);
    try {
      const offset = reset ? 0 : offsetRef.current;
      const res = await activityApi.getProjectActivity(project.id, { limit: PAGE_SIZE, offset });
      const data = res.data || [];
      setLogs(prev => reset ? data : [...prev, ...data]);
      offsetRef.current = offset + data.length;
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to load activity log', err);
    } finally {
      setLoading(false);
    }
  }, [project]);

  useEffect(() => {
    if (project) {
      offsetRef.current = 0;
      setLogs([]);
      setHasMore(true);
      fetchPage(true);
    }
  }, [project, fetchPage]);

  if (!project) return null;

  const grouped = groupByDate(logs, formatDate, t);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-96 max-w-full bg-popover border-l border-border flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-amber-700 dark:text-amber-400" />
            <div>
              <h2 className="text-sm font-semibold text-foreground">{t('projects.activity_log')}</h2>
              <p className="text-xs text-muted-foreground truncate max-w-[220px]">{project.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
          {loading && logs.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Activity className="w-8 h-8 text-muted-foreground/70 mb-2" />
              <p className="text-sm text-muted-foreground">{t('projects.activity_empty')}</p>
              <p className="text-xs text-muted-foreground/70 mt-1">{t('projects.activity_empty_description')}</p>
            </div>
          ) : (
            <>
              {grouped.map(([dateLabel, entries]) => (
                <div key={dateLabel}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{dateLabel}</p>
                  <div className="space-y-3">
                    {entries.map(log => (
                      <LogEntry key={log.id} log={log} />
                    ))}
                  </div>
                </div>
              ))}

              {hasMore && (
                <button
                  onClick={() => fetchPage(false)}
                  disabled={loading}
                  className="w-full py-2 text-xs text-muted-foreground hover:text-foreground border border-border rounded-lg hover:border-input transition-colors disabled:opacity-50"
                >
                  {loading ? t('common.loading') : t('notifications.load_more')}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
